import { ICondition } from './Condition';
import { IAutomationVariable } from './AutomationVariable';
import { UpdateMemberDataActionTypes } from './ActionOption';

export enum AutomationNodeType {
  ACTION = 'ACTION',
  CONDITION = 'CONDITION',
  DELAY = 'DELAY',
  JUMP = 'JUMP',
}

export enum AutomationActionNodeSubType {
  DEBUG = 'DEBUG',
  SEND_EMAIL = 'SEND_EMAIL',
  HANDLE_AUTOMATED_PAYOUT = 'HANDLE_AUTOMATED_PAYOUT',
  HANDLE_DISCOUNT_CODE_BOOST = 'HANDLE_DISCOUNT_CODE_BOOST',
  HANDLE_PFA_BOOST = 'HANDLE_PFA_BOOST',
  SEND_STA_EMAILS = 'SEND_STA_EMAILS',
  HANDLE_CASHBACK_BOOST = 'HANDLE_CASHBACK_BOOST',
  UPDATE_MEMBER_DATA = 'UPDATE_MEMBER_DATA',
  ADD_TO_OFFER = 'ADD_TO_OFFER',
  SEND_OFFER_DETAILS = 'SEND_OFFER_DETAILS',
  SEND_PRODUCT_CATALOG = 'SEND_PRODUCT_CATALOG',
}

export enum AutomationConditionNodeSubType {
  IF_ELSE_CONDITION = 'IF_ELSE_CONDITION',
}

export type AutomationNodeSubType = AutomationActionNodeSubType | AutomationConditionNodeSubType;

export enum AutomationNodeGroupType {
  EMAIL_SEQUENCE = 'EMAIL_SEQUENCE',
  GENERIC = 'GENERIC',
}

// TODO(jb) refactor to be a union type of all possible node types
export interface IAutomationNode {
  id?: string;
  type: AutomationNodeType;
  subType?: AutomationNodeSubType;
  parentId?: string;
  targetId?: string;
  children: IAutomationNode[]; // 'if true' branch for CONDITION, next steps for others
  elseChildren?: IAutomationNode[]; // NEW: 'else' branch specifically for CONDITION nodes
  edgeLabel?: string; // lable of the edge connecting this child from parent
  metadata: IAutomationNodeMetadata;
  variables: IAutomationVariable[];
}

export interface IAutomationNodeMetadata {
  group?: IAutomationNodeGroup;
  // TODO(jb) remove any and replace with explicit types
  [key: string]: any;
}

export interface IAutomationNodeGroup {
  id: string;
  type: AutomationNodeGroupType;
}

export enum AutomationNodeTraversalDirection {
  ANCESTORS = 'ANCESTORS',
  DESCENDANTS = 'DESCENDANTS',
}

export interface IAutomationNodeFilters {
  id?: string;
  type?: AutomationNodeType;
  subType?: AutomationNodeSubType;

  // NOTE: Not intended for database queries, but for filtering in-memory data
  relativeTo?: string;
  traversalDirection?: AutomationNodeTraversalDirection;
  depth?: number;
}

// #region Debug
export interface IAutomationDebugNode extends IAutomationNode {
  type: AutomationNodeType.ACTION;
  subType: AutomationActionNodeSubType.DEBUG;
  metadata: IAutomationDebugNodeMetadata;
}

export interface IAutomationDebugNodeMetadata extends IAutomationNodeMetadata {
  message: string;
}
// #endregion

// #region Send Email
export interface IAutomationSendEmailNode extends IAutomationNode {
  type: AutomationNodeType.ACTION;
  subType: AutomationActionNodeSubType.SEND_EMAIL;
  metadata: IAutomationSendEmailNodeMetadata;
}

export interface IAutomationSendEmailNodeMetadata extends IAutomationNodeMetadata {
  fromPreviousSender: boolean;
  templateId: number | null;
  resourceId: number | null;
}
// #endregion

// #region Condition
export interface IAutomationConditionNode extends IAutomationNode {
  type: AutomationNodeType.CONDITION;
  metadata: IAutomationConditionNodeMetadata;
}

export interface IAutomationConditionNodeMetadata extends IAutomationNodeMetadata {
  condition: ICondition;
}
// #endregion

// #region Delay
export interface IAutomationDelayNode extends IAutomationNode {
  type: AutomationNodeType.DELAY;
  metadata: IAutomationDelayNodeMetadata;
}

export interface IAutomationDelayNodeMetadata extends IAutomationNodeMetadata {
  durationMs: number;
}

export interface IAutomationAddToOfferNodeMetadata extends IAutomationNodeMetadata {
  offerId: number;
  // composite email support
  sendEmail?: boolean;
  templateId?: number | null;
  resourceId?: number | null;
}

export interface IAutomationSendProductCatalogNodeMetadata extends IAutomationNodeMetadata {
  brandCatalogId: number;
  selectionRuleId?: number;
  // composite email support
  sendEmail?: boolean;
  templateId?: number | null;
  resourceId?: number | null;
}

export interface IAutomationUpdateMemberDataNodeMetadata extends IAutomationNodeMetadata {
  type: UpdateMemberDataActionTypes;
  fieldId: number;
  fieldValue: string;
  customFields?: Array<{ fieldId: string, fieldValue: string }>;
}
// #endregion

// #region Sales Tracking Automations
export interface IAutomationSalesTrackingAutomationsNode extends IAutomationNode {
  type: AutomationNodeType.ACTION;
  subType: AutomationActionNodeSubType;
  metadata: IAutomationSalesTrackingAutomationsNodeMetadata;
}

export interface IAutomationSalesTrackingAutomationsNodeMetadata extends IAutomationNodeMetadata {
  offerType: string;
  payoutId: number;
}
// #endregion
